import { state$ } from "@internal/auth";
import {
  addRxPlugin,
  createRxDatabase,
  type RxCollection,
  type RxDatabase,
  type RxStorage,
} from "rxdb";
import { RxDBDevModePlugin } from "rxdb/plugins/dev-mode";
import { RxDBLocalDocumentsPlugin } from "rxdb/plugins/local-documents";
import { RxDBMigrationSchemaPlugin } from "rxdb/plugins/migration-schema";
import { getRxStorageDexie } from "rxdb/plugins/storage-dexie";
import { wrappedValidateAjvStorage } from "rxdb/plugins/validate-ajv";
import {
  createVersionAwareConflictHandler,
  trackDocumentVersion,
} from "./documentVersion";

addRxPlugin(RxDBMigrationSchemaPlugin);
// Local documents carry replication state between tabs: only the elected
// leader replicates, so the others learn whether work is saved by observing
// what it writes here.
addRxPlugin(RxDBLocalDocumentsPlugin);

let devModeAdded = false;
function ensureDevMode(): void {
  if (devModeAdded) return;
  if (process.env.NODE_ENV !== "production") {
    addRxPlugin(RxDBDevModePlugin);
  }
  devModeAdded = true;
}

export interface PerUserDbConfig<
  TCollections extends Record<string, RxCollection>,
> {
  namePrefix: string;
  collections: {
    [K in keyof TCollections]: {
      schema: unknown;
      // biome-ignore lint/suspicious/noExplicitAny: forwarded to rxdb's addCollections.
      migrationStrategies?: Record<number, (oldDoc: any) => any>;
    };
  };
}

export interface PerUserDb<TCollections extends Record<string, RxCollection>> {
  getDbForUser: (userId: string) => Promise<RxDatabase<TCollections>>;
  getDb: () => Promise<RxDatabase<TCollections>>;
  setDbUser: (userId: string | null) => void;
}

export function createPerUserDb<
  TCollections extends Record<string, RxCollection>,
>(config: PerUserDbConfig<TCollections>): PerUserDb<TCollections> {
  const dbPromises = new Map<string, Promise<RxDatabase<TCollections>>>();

  let currentUserId: string | null = null;
  let pendingResolvers: Array<(userId: string) => void> = [];

  function setDbUser(userId: string | null): void {
    currentUserId = userId;
    if (userId) {
      const resolvers = pendingResolvers;
      pendingResolvers = [];
      for (const resolve of resolvers) resolve(userId);
    }
  }

  function awaitUserId(): Promise<string> {
    if (currentUserId) return Promise.resolve(currentUserId);
    return new Promise<string>((resolve) => {
      pendingResolvers.push(resolve);
    });
  }

  function getDbForUser(userId: string): Promise<RxDatabase<TCollections>> {
    let p = dbPromises.get(userId);
    if (!p) {
      ensureDevMode();
      p = (async () => {
        const isDev = process.env.NODE_ENV !== "production";
        const baseStorage = getRxStorageDexie();
        const storage: RxStorage<unknown, unknown> = isDev
          ? wrappedValidateAjvStorage({ storage: baseStorage })
          : baseStorage;
        const db = await createRxDatabase<TCollections>({
          name: `${config.namePrefix}-${userId}`,
          storage,
          ignoreDuplicate: isDev,
        });
        const collections = Object.fromEntries(
          Object.entries(config.collections).map(([name, def]) => [
            name,
            {
              ...(def as object),
              localDocuments: true,
              // Why every collection needs this, and why the default handler
              // silently loses edits without it: see documentVersion.ts.
              conflictHandler: createVersionAwareConflictHandler(),
            },
          ]),
        );
        // biome-ignore lint/suspicious/noExplicitAny: rxdb's addCollections generic is hard to satisfy through a generic wrapper.
        await db.addCollections(collections as any);
        for (const name of Object.keys(config.collections)) {
          trackDocumentVersion(
            db.collections[name] as unknown as Parameters<
              typeof trackDocumentVersion
            >[0],
          );
        }
        return db;
      })();
      dbPromises.set(userId, p);
    }
    return p;
  }

  async function getDb(): Promise<RxDatabase<TCollections>> {
    const userId = await awaitUserId();
    return getDbForUser(userId);
  }

  // Bind to auth state so apps don't need to wire setDbUser manually.
  state$.subscribe((s) => {
    if (s.loading) return;
    setDbUser(s.user?.id ?? null);
  });

  return { getDbForUser, getDb, setDbUser };
}
